import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type ContextMenuAnchor = "top-left" | "bottom-right";

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  anchor?: ContextMenuAnchor;
  children: ReactNode;
}

const VIEWPORT_MARGIN = 4;

function clampPos(
  anchor: ContextMenuAnchor,
  x: number,
  y: number,
  width: number,
  height: number,
): { x: number; y: number } {
  let nextX = anchor === "bottom-right" ? x - width : x;
  let nextY = anchor === "bottom-right" ? y - height : y;
  nextX = Math.max(
    VIEWPORT_MARGIN,
    Math.min(nextX, window.innerWidth - width - VIEWPORT_MARGIN),
  );
  nextY = Math.max(
    VIEWPORT_MARGIN,
    Math.min(nextY, window.innerHeight - height - VIEWPORT_MARGIN),
  );
  return { x: nextX, y: nextY };
}

export function ContextMenu({
  x,
  y,
  onClose,
  anchor = "top-left",
  children,
}: ContextMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    setPos(null);
  }, [x, y, anchor]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = clampPos(anchor, x, y, rect.width, rect.height);
    if (!pos || next.x !== pos.x || next.y !== pos.y) {
      setPos(next);
    }
  });

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const style: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y }
    : { left: x, top: y, visibility: "hidden" };

  return createPortal(
    <div
      ref={ref}
      className="context-menu"
      role="menu"
      style={style}
      onContextMenu={(e) => e.preventDefault()}
    >
      {children}
    </div>,
    document.body,
  );
}

interface ContextMenuRowProps {
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

export function ContextMenuRow({
  onClick,
  active,
  danger,
  disabled,
  children,
}: ContextMenuRowProps) {
  const classes = ["context-menu__row"];
  if (active) classes.push("context-menu__row--active");
  if (danger) classes.push("context-menu__row--danger");
  return (
    <button
      type="button"
      role="menuitem"
      className={classes.join(" ")}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export function ContextMenuSeparator() {
  return <div className="context-menu__sep" role="separator" />;
}

interface ContextMenuHeaderProps {
  children: ReactNode;
}

export function ContextMenuHeader({ children }: ContextMenuHeaderProps) {
  return <div className="context-menu__header">{children}</div>;
}

interface ContextMenuSubmenuRowProps {
  label: ReactNode;
  disabled?: boolean;
  children: ReactNode;
}

const SUBMENU_CLOSE_DELAY_MS = 180;

export function ContextMenuSubmenuRow({
  label,
  disabled,
  children,
}: ContextMenuSubmenuRowProps) {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const [topOffset, setTopOffset] = useState(-4);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const subRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const cancelClose = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, SUBMENU_CLOSE_DELAY_MS);
  };

  useEffect(() => cancelClose, []);

  useLayoutEffect(() => {
    if (!open || !rowRef.current || !subRef.current) return;
    const rowRect = rowRef.current.getBoundingClientRect();
    const subRect = subRef.current.getBoundingClientRect();
    setFlip(
      rowRect.right + subRect.width > window.innerWidth - VIEWPORT_MARGIN,
    );
    const overflowBottom =
      rowRect.top + subRect.height > window.innerHeight - VIEWPORT_MARGIN;
    if (overflowBottom) {
      const maxAllowed =
        window.innerHeight - VIEWPORT_MARGIN - subRect.height - rowRect.top;
      setTopOffset(Math.min(-4, maxAllowed));
    } else {
      setTopOffset(-4);
    }
  });

  const classes = ["context-menu__row", "context-menu__row--submenu"];
  if (disabled) classes.push("context-menu__row--disabled");

  return (
    <div
      ref={rowRef}
      className={classes.join(" ")}
      role="menuitem"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-disabled={disabled || undefined}
      onMouseEnter={() => {
        if (disabled) return;
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <span className="context-menu__row-label">{label}</span>
      <span className="context-menu__chevron" aria-hidden>
        ›
      </span>
      {open && (
        <div
          ref={subRef}
          className={`context-menu__submenu${flip ? " context-menu__submenu--left" : ""}`}
          role="menu"
          style={{ top: topOffset }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {children}
        </div>
      )}
    </div>
  );
}
